import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { Paginated, PaginationQuery } from "../../types";
import useApi, { buildUrl } from "../api";
import { CustomError } from "../errors";
import { ProcessingRequestDocument, RenameRequestDocument } from "./types";

export const useRequestDocumentApi = () => {
  const api = useApi();
  const baseurl = "/processing_request_documents";
  const processingRequestUrl = "/processing_requests";

  const useRequestDocuments = useCallback(
    (query: PaginationQuery, requestID: number) => {
      const url = buildUrl(`${processingRequestUrl}/${requestID}/documents`, query);
      const result = useQuery<Paginated<ProcessingRequestDocument>, CustomError>({
        queryKey: [url],
        queryFn: ({ signal }) => api.get<Paginated<ProcessingRequestDocument>>({ url, signal }),
      });
      return { ...result, error: result.error ?? undefined };
    },
    [api],
  );

  const insertProcessingRequestDocument = useCallback(
    async (file: File, overWrite?: boolean, requestID?: number) => {
      const formData = new FormData();
      formData.append("file", file);

      await api.post<FormData, { id: string }>(
        `${processingRequestUrl}/${requestID}/documents${overWrite ? "?overwrite=1" : ""}`,
        formData,
      );
      api.mutateCache([`${processingRequestUrl}/${requestID}`, `${processingRequestUrl}/${requestID}/documents`]);
    },
    [api],
  );

  const renameProcessingRequestDocument = useCallback(
    async (body: RenameRequestDocument, documentID: string, requestID: number) => {
      await api.put<RenameRequestDocument>(`${baseurl}/${documentID}`, body);
      api.mutateCache([`${processingRequestUrl}/${requestID}/documents`]);
    },
    [api],
  );

  const deleteProcessingRequestDocument = useCallback(
    async (documentID: string, requestID: number) => {
      const url = `${baseurl}/${documentID}`;
      await api.del(url);
      api.mutateCache([`${processingRequestUrl}/${requestID}/documents`]);
    },
    [api],
  );

  return {
    useRequestDocuments,
    insertProcessingRequestDocument,
    renameProcessingRequestDocument,
    deleteProcessingRequestDocument,
  };
};
